import * as SerializerLib from "jsonapi-serializer";

import { ResponsePayload } from "@/types/model";
import { Layer, Place } from "@/types/places";
import { deleteData, getData, postData, putData } from "../base.ehq.model";
import { deserialize, serializeError } from "../base.serializer";

const JSONAPISerializer = SerializerLib.Serializer;

interface IndexType {
  [key: string | number]: any;
}

const opts = {
  attributes: ["marker", "marker-response"]
};

export const serialize = (data) => {
  try {
    const Serializer = new JSONAPISerializer("markers", opts);
    return Serializer.serialize(data);
  } catch (err) {
    return serializeError(err);
  }
};

export const handlePostMarker = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  body: { marker: IndexType; "marker-response": IndexType },
  params?: object
) => {
  const endpoint = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers`;
  const serializedBody = await serialize(body);
  return await postData(token, endpoint, params, serializedBody);
};

export const handleEditMarker = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  body: { marker: IndexType; "marker-response": IndexType },
  params?: object
) => {
  const endpoint = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}`;
  const serializedBody = await serialize(body);
  return await putData(token, endpoint, params, serializedBody);
};

export const getMarkerCategories = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  params?: object,
  config?: object
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/marker_categories`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const fetchQuestions = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  params?: object,
  config?: object
): Promise<any[]> => {
  const endpoint = `${baseUrl}/projects/${projectId}/maps/${placesId}/questions`;
  return await getData(token, endpoint, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getPlace = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  params?: object,
  config?: object
): Promise<Place> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getLayers = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  params?: object,
  config?: object
): Promise<Layer[]> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/layers`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getPinDetails = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  params?: any,
  config?: any
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers`;
  const extendedParams = { ...params, not_deleted: true };

  return await getData(token, queryPath, extendedParams, config).then(
    async (response: any) => {
      const data = await deserialize(response.data);
      return {
        data,
        meta: {
          count: response?.data?.meta?.count || 0,
          totalMarkersCount: response.data.meta["total-markers-count"]
        }
      };
    }
  );
};

export const deletePin = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}`;
  const extendedParams = { ...params, soft_delete: true };

  return await deleteData(token, queryPath, extendedParams).then(
    (response: any) => deserialize(response.data)
  );
};

export const reportPin = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}/report`;

  return await putData(token, queryPath, params).then((response: any) =>
    deserialize(response.data)
  );
};

export const likePin = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}/votes`;

  return await postData(token, queryPath, params, {}).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const undoLikePin = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}/votes`;

  return await deleteData(token, queryPath, params).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getMarkerData = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}/marker_response`;

  return await getData(token, queryPath, params).then(
    (response: ResponsePayload) => {
      return deserialize(response.data);
    }
  );
};

export const getMarkerDetailsData = async (
  token: string,
  baseUrl: string,
  projectId: string,
  placesId: string,
  markerId: string,
  params?: any,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/maps/${placesId}/markers/${markerId}`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => {
      return deserialize(response.data);
    }
  );
};
